import { useCallback } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { v4 as uuidv4 } from 'uuid';

import { CARD_TYPE } from '#client/constants/cards';
import { ORDER_PHYSICAL_DEBIT_CARD } from '#client/constants/nextUpCards';
import useCardsContext from '#client/hooks/useCardsContext';
import useConst from '#client/hooks/useConst';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useNextUpCardsContext from '#client/hooks/useNextUpCardsContext';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { CREATE_CARD_MUTATION } from '#client/mutations';

export default function useOrderCard() {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const idempotencyKey = useConst(uuidv4);
  const { unitAccountId } = useUnitAccountId();
  const { refetchCard } = useCardsContext();
  const { createSuccessToast, createErrorToast } = useToastContext();
  const { removeCardWithAnimation } = useNextUpCardsContext();

  const [createCard, { loading }] = useMutation(CREATE_CARD_MUTATION, {
    noThrow: true
  });

  const handleSubmitAddress = useCallback(
    async (address) => {
      const { error: createCardError } = await createCard({
        variables: {
          input: {
            identifier: {
              businessUuid: businessEntityUuid,
              unitAccountId
            },
            attributes: {
              type: CARD_TYPE.BUSINESS_DEBIT_CARD,
              idempotencyKey,
              shippingAddress: address
            }
          }
        },
        skip: !unitAccountId
      });

      if (createCardError) {
        createErrorToast({
          title: 'Error',
          description: 'Failed to request new card',
          error: createCardError
        });
        return;
      }

      // Show toast success
      createSuccessToast({
        title: 'Your new card is on the way',
        description: 'It’ll be there in 7 to 10 business days.'
      });
      refetchCard();
      removeCardWithAnimation(ORDER_PHYSICAL_DEBIT_CARD);
    },
    [
      businessEntityUuid,
      createCard,
      createErrorToast,
      createSuccessToast,
      idempotencyKey,
      refetchCard,
      removeCardWithAnimation,
      unitAccountId
    ]
  );

  return {
    handleSubmitAddress,
    loading
  };
}
